
import { useCallback, useRef, useState } from "react";
import "swiper/css";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

export const HOME_CAROUSEL_CARD_CLASS = "h-[300px] md:h-[340px]";

function NavBtn({ label, onClick, disabled, className, flipIcon }) {
	return (
		<button
			type="button"
			aria-label={label}
			disabled={disabled}
			onClick={onClick}
			className={[
				"absolute top-1/2 z-20 -translate-y-1/2",
				"flex h-10 w-10 md:h-11 md:w-11 items-center justify-center",
				"rounded-full border-2 border-darkBlue bg-darkBlue text-white",
				"shadow-[0_6px_20px_rgba(39,106,166,0.25)]",
				"transition-all hover:bg-[#1e4570] hover:border-cta hover:scale-105",
				"disabled:opacity-0 disabled:pointer-events-none",
				className,
			].join(" ")}
		>
			<svg
				width="22"
				height="22"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2.2"
				strokeLinecap="round"
				strokeLinejoin="round"
				className={flipIcon ? "rotate-180" : ""}
				aria-hidden
			>
				<path d="M15 18l-6-6 6-6" />
			</svg>
		</button>
	);
}

function slidesForWidth(slideCount, target) {
	if (slideCount <= 0) return 1;
	return Math.min(target, slideCount);
}

export default function HomeTagCarousel({
	slides,
	swiperClassName = "home-tag-swiper",
}) {
	const swiperRef = useRef(null);
	const slideCount = slides.length;
	const canLoop = slideCount > 3;

	const [nav, setNav] = useState({ prev: false, next: true });

	const syncNav = useCallback(
		(swiper) => {
			if (!swiper || canLoop) {
				setNav({ prev: true, next: true });
				return;
			}
			setNav({
				prev: !swiper.isBeginning,
				next: !swiper.isEnd,
			});
		},
		[canLoop]
	);

	return (
		<div className="relative w-full">
			<div
				className="mb-3 flex h-[3px] max-w-[88px] overflow-hidden rounded-full opacity-90"
				aria-hidden
			>
				<div className="flex-[3] bg-darkBlue" />
				<div className="flex-1 bg-cta" />
			</div>

			<div className="relative px-12 md:px-14">
				{slideCount > 1 && (
					<>
						<NavBtn
							label="اسلاید بعدی"
							className="right-0 md:right-1"
							disabled={!nav.next}
							flipIcon
							onClick={() => swiperRef.current?.slideNext()}
						/>
						<NavBtn
							label="اسلاید قبلی"
							className="left-0 md:left-1"
							disabled={!nav.prev}
							onClick={() => swiperRef.current?.slidePrev()}
						/>
					</>
				)}

				<div className="overflow-hidden rounded-2xl">
					<Swiper
						dir="rtl"
						modules={[Autoplay]}
						className={`!w-full ${swiperClassName}`}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
							syncNav(swiper);
						}}
						onSlideChange={(swiper) => syncNav(swiper)}
						onReachBeginning={(swiper) => syncNav(swiper)}
						onReachEnd={(swiper) => syncNav(swiper)}
						loop={canLoop}
						slidesPerView={slidesForWidth(slideCount, 1)}
						spaceBetween={14}
						autoplay={
							slideCount > 1
								? {
										delay: 5500,
										disableOnInteraction: true,
									}
								: false
						}
						breakpoints={{
							768: {
								slidesPerView: slidesForWidth(slideCount, 2),
								spaceBetween: 16,
							},
							1024: {
								slidesPerView: slidesForWidth(slideCount, 3),
								spaceBetween: 16,
							},
						}}
					>
						{slides.map((slide) => (
							<SwiperSlide key={slide.key} className="!h-auto [&>*]:h-full">
								{slide.node}
							</SwiperSlide>
						))}
					</Swiper>
				</div>
			</div>
		</div>
	);
}
